import { useReadOnly } from 'slate-react'
import { sectionTypeMatches, useAgentDefinitions, usePlugins } from '@tunasong/plugin-lib'
import type { BlockAction } from '@tunasong/plugin-lib'
import { useSelector, useThunkDispatch } from '@tunasong/redux'
import { useCmdHotkey, useTunaMediaQuery } from '@tunasong/ui-lib'
import type { BlockRef } from '@tunasong/schemas'
import { useAgentChildren } from './use-agent-children.js'
import { setActiveInlineTurn, startInlineAgentTurn } from './inline/inline-agent-turn.js'
import { resolveBlockAnchorEl } from './inline/resolve-block-anchor.js'
import { openInlineAsk } from './inline/inline-ask.js'
import {
  askHereCommandId,
  askHereDefaultHotkey,
  askHereLabel,
  openAskHere,
  resolveAskAgent,
} from './inline/ask-here.js'
import { isAgentAnswering } from './select-agent-turn.js'
import { getAgentDisplayName } from './agent-visuals.js'

/**
 * Aggregate per-block affordances for a given `BlockRef`. Combines:
 *
 *  - agent-contributed `AgentDefinition.blockAffordances` for every agent
 *    currently attached to the host entity (matched by `agent.definitionId`),
 *    filtered by `blockTypes` (with `'*'` meaning any kind); affordances with
 *    `sectionTypes` are additionally gated by `block.sectionType` (fail-open
 *    via `sectionTypeMatches`);
 *  - generic `TunaPlugin.getBlockActions(block)` contributions from every
 *    registered plugin (e.g., chord plugin's own "Transpose").
 *
 * Agent items dispatch `openAgentComposer` on select — surfacing the Agents
 * sidebar focused on the chosen agent with the targeted block preset as the
 * composer's focus and an optional draft pre-filled from
 * `buildAsk(block).messageTemplate`. Plugin items use their own `onSelect`.
 *
 * Pure read hook: no side effects on mount, no fetches. Re-renders when the
 * underlying graph cache, plugin set, or agent definitions change.
 */
export interface BlockActionsOptions {
  /**
   * The block's enclosing refs, nearest-first (the block, its line, its section), read when "Ask here" is chosen
   * so the docked chip can say "About: Verse 2". Lazy because every chord renders a menu (Joni, #2503).
   */
  context?: () => readonly BlockRef[]
}

export const useBlockActions = (block: BlockRef, options: BlockActionsOptions = {}): BlockAction[] => {
  const attachedAgents = useAgentChildren(block.entityId)
  const agentDefs = useAgentDefinitions()
  const plugins = usePlugins('all')
  const dispatch = useThunkDispatch()
  const activeAgentId = useSelector(state => state.ui.sidebar.activeAgentId)
  const askAgent = resolveAskAgent(attachedAgents, activeAgentId)
  const answering = useSelector(state => isAgentAnswering(state, askAgent?.id))
  const askHotkey = useCmdHotkey(askHereCommandId)?.hotkey ?? askHereDefaultHotkey
  // On a phone, read-first mode keeps the sidebar chat: an edit ghost can't be resolved in a read-only view (Joni)
  const isPhone = useTunaMediaQuery('(max-width: 599.95px)')
  const readOnly = useReadOnly()
  const offersAskHere = Boolean(askAgent) && !(isPhone && readOnly)

  // "Ask <name> here…" (spec InlineAskField; Joni): hidden with no agent attached; disabled, saying why,
  // while that agent is still answering. The hint shows the user's own binding for the same Command.
  const askHere: BlockAction[] =
    askAgent && offersAskHere
      ? [
          {
            id: askHereCommandId,
            label: askHereLabel(askAgent, answering),
            shortcut: askHotkey,
            disabled: answering,
            onSelect: () => {
              if (!answering) {
                openAskHere(askAgent, block, options.context?.() ?? [block])
              }
            },
          },
        ]
      : []

  const fromAgents = attachedAgents.flatMap<BlockAction>(agent => {
    const definitionId = agent.definitionId
    if (definitionId === undefined) {
      return []
    }
    const def = agentDefs.find(d => d.id === definitionId)
    const affordances = def?.blockAffordances ?? []
    return affordances
      .filter(a => a.blockTypes === '*' || a.blockTypes.includes(block.type))
      .filter(a => sectionTypeMatches(a, block))
      .map(a => ({
        id: `${agent.id}/${a.id}`,
        label: a.label,
        iconKey: a.iconKey,
        onSelect: () => {
          const { messageTemplate, focus } = a.buildAsk(block)
          // No canned ask: open the inline ask field at the block for the musician to type into,
          // rather than a dead click (spec InlineAskField). Templated actions stay one-click.
          if (!messageTemplate) {
            openInlineAsk({
              agentId: agent.id,
              agentName: getAgentDisplayName(agent),
              focus,
              trigger: { type: 'change', reason: a.id },
              anchorEl: resolveBlockAnchorEl(block.id),
            })
            return
          }
          // In-context: run an interactive inline turn and register it so the
          // answer/edit renders inline at the block — never the sidebar.
          const { requestId, turnId } = startInlineAgentTurn(
            { dispatch },
            { agentId: agent.id, message: messageTemplate, focus, trigger: { type: 'change', reason: a.id } }
          )
          setActiveInlineTurn({ agentId: agent.id, requestId, turnId, anchorEl: resolveBlockAnchorEl(block.id) })
        },
      }))
  })

  const fromPlugins = (plugins ?? []).flatMap(p => p.getBlockActions?.(block) ?? [])

  return [...fromAgents, ...askHere, ...fromPlugins]
}
